import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Loader2, Plus, Save, Download, Printer, Mail, Upload, Copy, CalendarPlus,
  ChevronLeft, Check, Lock,
} from "lucide-react";
import { API, fmtDate } from "./shared";

const inputCls =
  "bg-white border border-[#BCB3A6]/50 rounded-2xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480] w-full";

const SECTIONS = [
  { key: "session_info", title: "1. Session Information", fields: [
    ["date", "Date", "date"], ["time", "Time", "time"], ["duration", "Duration (e.g. 60 min)"],
    ["clinic_name", "Clinic Name"], ["provider_name", "Provider Name"], ["visit_type", "Visit Type"],
  ]},
  { key: "goals", title: "2. Patient Goals & Concerns", fields: [
    ["main_concerns", "Main Concerns Discussed", "textarea"], ["fertility_goals", "Fertility Goals", "textarea"],
    ["questions_asked", "Questions Asked", "textarea"], ["emotional_concerns", "Emotional Concerns", "textarea"],
  ]},
  { key: "recommendations", title: "3. Provider Recommendations", fields: [
    ["tests_ordered", "Tests Ordered", "textarea"], ["results_reviewed", "Results Reviewed", "textarea"],
    ["recommendations", "Recommendations", "textarea"], ["follow_up_instructions", "Follow-Up Instructions", "textarea"],
  ]},
  { key: "treatment", title: "4. Treatment Plan", fields: [
    ["recommended_treatment", "Recommended Treatment"], ["care_plan_notes", "Care Plan Notes", "textarea"],
    ["timeline", "Timeline"], ["next_steps", "Next Steps", "textarea"],
  ]},
  { key: "summary", title: "6. Appointment Summary", fields: [
    ["key_info_translated", "Key Information Translated", "textarea"],
    ["recommendations_explained", "Provider Recommendations Explained", "textarea"],
    ["questions_asked", "Questions Asked", "textarea"], ["provider_responses", "Provider Responses", "textarea"],
  ]},
  { key: "financial", title: "7. Financial Notes", fields: [
    ["costs_discussed", "Costs Discussed", "textarea"], ["insurance_notes", "Insurance Notes", "textarea"],
    ["financing_information", "Financing Information", "textarea"],
  ]},
  { key: "followup", title: "8. Follow-Up Actions", fields: [
    ["client_responsibilities", "Client Responsibilities", "textarea"],
    ["clinic_responsibilities", "Clinic Responsibilities", "textarea"], ["deadlines", "Deadlines"],
  ]},
  { key: "wellness", title: "9. Emotional Wellness", fields: [
    ["emotional_status", "Emotional Status", "textarea"], ["support_resources", "Support Resources Recommended", "textarea"],
  ]},
  { key: "consultant", title: "10. Soarlife Consultant Notes", fields: [
    ["interpretation_summary", "Interpretation Summary", "textarea"],
    ["language_considerations", "Language Considerations", "textarea"],
    ["cultural_considerations", "Cultural Considerations", "textarea"],
    ["additional_notes", "Additional Notes", "textarea"],
  ]},
  { key: "next_appointment", title: "11. Next Appointment", fields: [
    ["date", "Date", "date"], ["time", "Time", "time"], ["provider", "Provider"], ["purpose", "Purpose"],
  ]},
];

const EMPTY_NOTE = {
  client_email: "", client_name: "", partner_name: "", consultant_name: "", clinic_email: "",
  sections: {}, scores: { understanding: "", confidence: "", remaining_concerns: "", follow_up_required: false },
  visible_to_client: false, status: "draft",
};

export function NotesTab({ authHeader }) {
  const [notes, setNotes] = useState(null);
  const [editing, setEditing] = useState(null); // note object being edited
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/consulting-notes`, {
        headers: authHeader(),
        params: { status: statusFilter || undefined, q: search || undefined },
      });
      setNotes(res.data);
    } catch {}
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  if (editing) {
    return <NoteEditor note={editing} authHeader={authHeader}
      onClose={() => { setEditing(null); load(); }} />;
  }

  const filtered = (notes || []).filter((n) =>
    !search || `${n.client_name} ${n.client_email}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="notes-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-serif text-2xl text-[#3F5043]">Soarlife Consulting Notes</div>
          <p className="text-sm text-[#3F5043]/70">Complete appointment notes digitally during sessions — saved automatically.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY_NOTE, id: null })} data-testid="note-new-btn"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#875F45] transition-colors">
          <Plus size={13} /> New session note
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <input placeholder="Search client…" value={search} data-testid="notes-search"
          onChange={(e) => setSearch(e.target.value)} className={`${inputCls} w-56`} />
        <select value={statusFilter} data-testid="notes-status-filter"
          onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} w-40`}>
          <option value="">All</option>
          <option value="draft">Drafts</option>
          <option value="final">Final</option>
        </select>
      </div>

      {!notes ? (
        <div className="text-center py-10"><Loader2 size={18} className="animate-spin inline text-[#899480]" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-8 text-center text-sm text-[#899480]" data-testid="notes-empty">
          No session notes yet — start one above.
        </div>
      ) : (
        <div className="space-y-2" data-testid="notes-list">
          {filtered.map((n) => (
            <button key={n.id} onClick={() => setEditing(n)} data-testid={`note-row-${n.id}`}
              className="w-full text-left bg-white border border-[#BCB3A6]/40 rounded-2xl px-4 py-3 hover:shadow-md transition-all">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-medium text-[#3F5043] flex items-center gap-2">
                    {n.client_name}
                    <span className={`text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-full ${
                      n.status === "final" ? "bg-[#899480]/15 text-[#3F5043]" : "bg-[#D4AF37]/20 text-[#875F45]"}`}>
                      {n.status}
                    </span>
                    {n.visible_to_client && (
                      <span className="text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-full bg-[#3F5043]/10 text-[#3F5043]">
                        Client-visible
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#899480]">
                    {n.sections?.session_info?.date ? fmtDate(n.sections.session_info.date) : fmtDate(n.created_at)}
                    {n.consultant_name ? ` · ${n.consultant_name}` : ""}
                  </div>
                </div>
                {n.status === "final" && <Lock size={14} className="text-[#899480]" />}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NoteEditor({ note: initial, authHeader, onClose }) {
  const [note, setNote] = useState(initial.id ? initial : { ...EMPTY_NOTE });
  const [noteId, setNoteId] = useState(initial.id || null);
  const [saveState, setSaveState] = useState(""); // "" | saving | saved | error
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const timerRef = useRef(null);
  const skipNext = useRef(true);

  const payload = () => ({
    client_email: note.client_email, client_name: note.client_name,
    partner_name: note.partner_name, consultant_name: note.consultant_name,
    clinic_email: note.clinic_email, sections: note.sections, scores: note.scores,
    visible_to_client: note.visible_to_client, status: note.status,
    appointment_id: note.appointment_id || null,
  });

  const persist = async (status) => {
    const p = { ...payload(), ...(status ? { status } : {}) };
    if (noteId) {
      const res = await axios.patch(`${API}/admin/consulting-notes/${noteId}`, p, { headers: authHeader() });
      setNote((n) => ({ ...n, status: res.data.status }));
      return res.data;
    }
    const res = await axios.post(`${API}/admin/consulting-notes`, p, { headers: authHeader() });
    setNoteId(res.data.id);
    setNote(res.data);
    return res.data;
  };

  // autosave (drafts only) — debounced 1.5s after any change
  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    if (note.status === "final") { setSaveState(""); return; }
    if (!note.client_email || !note.client_name) return;
    setSaveState("saving");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        await persist();
        setSaveState("saved");
      } catch { setSaveState("error"); }
    }, 1500);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note]);

  const setSection = (key, field, value) =>
    setNote((n) => ({ ...n, sections: { ...n.sections, [key]: { ...(n.sections[key] || {}), [field]: value } } }));

  const setScore = (field, value) =>
    setNote((n) => ({ ...n, scores: { ...n.scores, [field]: value } }));

  const doAction = async (action, label) => {
    if (busy) return;
    if (!noteId) {
      setMsg("Save the note first (add client name + email).");
      return;
    }
    setBusy(action);
    setMsg("");
    try {
      if (action === "pdf" || action === "print") {
        const res = await axios.get(`${API}/admin/consulting-notes/${noteId}/pdf`, {
          headers: authHeader(), responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        if (action === "print") {
          const w = window.open(url, "_blank");
          if (w) w.onload = () => w.print();
        } else {
          const a = document.createElement("a");
          a.href = url;
          a.download = `Soarlife_Note_${note.client_name.replace(/\s/g, "_")}.pdf`;
          a.click();
        }
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } else if (action === "email-client" || action === "email-clinic") {
        const res = await axios.post(`${API}/admin/consulting-notes/${noteId}/email`,
          { target: action === "email-client" ? "client" : "clinic" }, { headers: authHeader() });
        setMsg(res.data.email === "sent" ? `Emailed to ${res.data.to}.`
          : "Email could not be delivered right now (service being restored) — download the PDF instead.");
      } else if (action === "upload") {
        await axios.post(`${API}/admin/consulting-notes/${noteId}/upload-to-profile`, {}, { headers: authHeader() });
        setMsg("PDF filed in the client's records vault.");
      } else if (action === "duplicate") {
        const res = await axios.post(`${API}/admin/consulting-notes/${noteId}/duplicate`, {}, { headers: authHeader() });
        setMsg("Duplicated as a new draft.");
        setNote(res.data);
        setNoteId(res.data.id);
      } else if (action === "followup") {
        const res = await axios.post(`${API}/admin/consulting-notes/${noteId}/followup-appointment`, {}, { headers: authHeader() });
        setMsg(`Follow-up appointment created: ${res.data.title} · ${res.data.date}`);
      }
    } catch (e) {
      setMsg(e?.response?.data?.detail || `${label} failed.`);
    } finally { setBusy(""); }
  };

  const saveFinal = async () => {
    if (busy) return;
    setBusy("final");
    try {
      await persist("final");
      setMsg("Final note saved and locked.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not save.");
    } finally { setBusy(""); }
  };

  const isFinal = note.status === "final";
  const meds = note.sections.medications?.items || [];

  const scoreInput = (field, label) => (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.15em] text-[#899480] mb-1">{label}</label>
      <input type="number" min="1" max="10" value={note.scores[field] ?? ""} disabled={isFinal}
        data-testid={`note-score-${field}`}
        onChange={(e) => setScore(field, e.target.value === "" ? "" : Number(e.target.value))}
        className={`${inputCls} w-24`} />
    </div>
  );

  return (
    <div data-testid="note-editor">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <button onClick={onClose} data-testid="note-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#899480] hover:text-[#3F5043]">
          <ChevronLeft size={14} /> All notes
        </button>
        <div className="text-xs" data-testid="note-save-state">
          {saveState === "saving" && <span className="text-[#899480] inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Saving…</span>}
          {saveState === "saved" && <span className="text-[#899480] inline-flex items-center gap-1"><Check size={11} /> Saved automatically</span>}
          {saveState === "error" && <span className="text-[#875F45]">Couldn't autosave — use Save Draft</span>}
          {isFinal && <span className="text-[#3F5043] inline-flex items-center gap-1"><Lock size={11} /> Final — locked</span>}
        </div>
      </div>

      {/* header fields */}
      <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <input required placeholder="Client name" value={note.client_name} disabled={isFinal}
          data-testid="note-client-name"
          onChange={(e) => setNote({ ...note, client_name: e.target.value })} className={inputCls} />
        <input required type="email" placeholder="Client email" value={note.client_email} disabled={isFinal}
          data-testid="note-client-email"
          onChange={(e) => setNote({ ...note, client_email: e.target.value })} className={inputCls} />
        <input placeholder="Partner name" value={note.partner_name} disabled={isFinal}
          data-testid="note-partner"
          onChange={(e) => setNote({ ...note, partner_name: e.target.value })} className={inputCls} />
        <input placeholder="Consultant name" value={note.consultant_name} disabled={isFinal}
          data-testid="note-consultant"
          onChange={(e) => setNote({ ...note, consultant_name: e.target.value })} className={inputCls} />
        <input placeholder="Clinic email (for sharing)" value={note.clinic_email} disabled={isFinal}
          data-testid="note-clinic-email"
          onChange={(e) => setNote({ ...note, clinic_email: e.target.value })} className={inputCls} />
        <label className="flex items-center gap-2 text-xs text-[#3F5043]">
          <input type="checkbox" checked={note.visible_to_client} disabled={isFinal}
            data-testid="note-visible-toggle"
            onChange={(e) => setNote({ ...note, visible_to_client: e.target.checked })}
            className="accent-[#3F5043] w-4 h-4" />
          Visible to client in their portal
        </label>
      </div>

      {/* 12 sections */}
      {SECTIONS.slice(0, 4).map((sec) => (
        <NoteSection key={sec.key} sec={sec} note={note} setSection={setSection} disabled={isFinal} />
      ))}

      {/* 5. Medication review — line items */}
      <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-5" data-testid="note-section-medications">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-3">5. Medication Review</div>
        {meds.map((m, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2" data-testid={`note-med-${i}`}>
            <input placeholder="Medication" value={m.name || ""} disabled={isFinal}
              onChange={(e) => {
                const items = meds.map((x, j) => (j === i ? { ...x, name: e.target.value } : x));
                setSection("medications", "items", items);
              }} className={inputCls} />
            <input placeholder="Dosage" value={m.dosage || ""} disabled={isFinal}
              onChange={(e) => {
                const items = meds.map((x, j) => (j === i ? { ...x, dosage: e.target.value } : x));
                setSection("medications", "items", items);
              }} className={inputCls} />
            <input type="date" value={m.start_date || ""} disabled={isFinal}
              onChange={(e) => {
                const items = meds.map((x, j) => (j === i ? { ...x, start_date: e.target.value } : x));
                setSection("medications", "items", items);
              }} className={inputCls} />
            <input placeholder="Instructions" value={m.instructions || ""} disabled={isFinal}
              onChange={(e) => {
                const items = meds.map((x, j) => (j === i ? { ...x, instructions: e.target.value } : x));
                setSection("medications", "items", items);
              }} className={inputCls} />
          </div>
        ))}
        {!isFinal && (
          <button onClick={() => setSection("medications", "items", [...meds, {}])} data-testid="note-add-med"
            className="text-xs text-[#3F5043] font-semibold inline-flex items-center gap-1 hover:text-[#875F45]">
            <Plus size={12} /> Add medication
          </button>
        )}
      </div>

      {SECTIONS.slice(4).map((sec) => (
        <NoteSection key={sec.key} sec={sec} note={note} setSection={setSection} disabled={isFinal} />
      ))}

      {/* 12. Session outcome */}
      <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-5" data-testid="note-section-outcome">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-3">12. Session Outcome</div>
        <div className="flex flex-wrap gap-4 items-end">
          {scoreInput("understanding", "Understanding (1-10)")}
          {scoreInput("confidence", "Confidence (1-10)")}
          {scoreInput("remaining_concerns", "Remaining concerns (1-10)")}
          <label className="flex items-center gap-2 text-xs text-[#3F5043] pb-2">
            <input type="checkbox" checked={!!note.scores.follow_up_required} disabled={isFinal}
              data-testid="note-followup-required"
              onChange={(e) => setScore("follow_up_required", e.target.checked)}
              className="accent-[#3F5043] w-4 h-4" />
            Follow-up required
          </label>
        </div>
      </div>

      {msg && <div className="mb-4 bg-[#899480]/10 border border-[#899480]/30 rounded-2xl p-3 text-sm text-[#3F5043]" data-testid="note-msg">{msg}</div>}

      {/* action bar */}
      <div className="flex flex-wrap gap-2 pb-10" data-testid="note-actions">
        {!isFinal && (
          <>
            <button onClick={async () => { setBusy("draft"); try { await persist("draft"); setMsg("Draft saved."); } catch { setMsg("Save failed."); } setBusy(""); }}
              data-testid="note-save-draft" disabled={!!busy}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#899480]/50 text-[#3F5043] text-xs uppercase tracking-[0.12em] font-semibold hover:bg-[#899480]/10 disabled:opacity-60">
              {busy === "draft" ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save draft
            </button>
            <button onClick={saveFinal} data-testid="note-save-final" disabled={!!busy}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.12em] font-semibold hover:bg-[#875F45] disabled:opacity-60">
              {busy === "final" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save final note
            </button>
          </>
        )}
        <button onClick={() => doAction("pdf", "Download")} data-testid="note-download-pdf" disabled={!!busy}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#899480]/50 text-[#3F5043] text-xs uppercase tracking-[0.12em] font-semibold hover:bg-[#899480]/10 disabled:opacity-60">
          <Download size={12} /> Download PDF
        </button>
        <button onClick={() => doAction("print", "Print")} data-testid="note-print-pdf" disabled={!!busy}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#899480]/50 text-[#3F5043] text-xs uppercase tracking-[0.12em] font-semibold hover:bg-[#899480]/10 disabled:opacity-60">
          <Printer size={12} /> Print
        </button>
        <button onClick={() => doAction("email-client", "Email")} data-testid="note-email-client" disabled={!!busy}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#D4AF37]/60 text-[#875F45] text-xs uppercase tracking-[0.12em] font-semibold hover:bg-[#D4AF37]/10 disabled:opacity-60">
          <Mail size={12} /> Email to client
        </button>
        <button onClick={() => doAction("email-clinic", "Email")} data-testid="note-email-clinic" disabled={!!busy}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#D4AF37]/60 text-[#875F45] text-xs uppercase tracking-[0.12em] font-semibold hover:bg-[#D4AF37]/10 disabled:opacity-60">
          <Mail size={12} /> Email to clinic
        </button>
        <button onClick={() => doAction("upload", "Upload")} data-testid="note-upload-profile" disabled={!!busy}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#899480]/50 text-[#3F5043] text-xs uppercase tracking-[0.12em] font-semibold hover:bg-[#899480]/10 disabled:opacity-60">
          <Upload size={12} /> Upload to client profile
        </button>
        <button onClick={() => doAction("duplicate", "Duplicate")} data-testid="note-duplicate" disabled={!!busy}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#899480]/50 text-[#3F5043] text-xs uppercase tracking-[0.12em] font-semibold hover:bg-[#899480]/10 disabled:opacity-60">
          <Copy size={12} /> Duplicate
        </button>
        <button onClick={() => doAction("followup", "Follow-up")} data-testid="note-followup-appt" disabled={!!busy}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#D4AF37] text-[#3F5043] text-xs uppercase tracking-[0.12em] font-semibold hover:bg-[#3F5043] hover:text-[#F8F7F3] transition-colors disabled:opacity-60">
          <CalendarPlus size={12} /> Create follow-up
        </button>
      </div>
    </div>
  );
}

function NoteSection({ sec, note, setSection, disabled }) {
  const data = note.sections[sec.key] || {};
  return (
    <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-5" data-testid={`note-section-${sec.key}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-3">{sec.title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {sec.fields.map(([field, label, type]) =>
          type === "textarea" ? (
            <textarea key={field} placeholder={label} rows={2} value={data[field] || ""} disabled={disabled}
              data-testid={`note-${sec.key}-${field}`}
              onChange={(e) => setSection(sec.key, field, e.target.value)}
              className={`${inputCls} resize-y sm:col-span-2`} />
          ) : (
            <input key={field} type={type || "text"} placeholder={label} value={data[field] || ""} disabled={disabled}
              data-testid={`note-${sec.key}-${field}`}
              onChange={(e) => setSection(sec.key, field, e.target.value)}
              className={inputCls} />
          )
        )}
      </div>
    </div>
  );
}
